// AdminLayout.tsx
import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { useAppSelector, useAppDispatch } from "../Modules/Presentation/hooks";
import { selectUser } from "../Modules/User/Presentation/selectors";
import { logout } from "../Modules/User/Presentation/reducers";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DownOutlined,
  RightOutlined,
  DashboardOutlined,
  NotificationOutlined,
  TeamOutlined,
  DatabaseOutlined,
  BarChartOutlined,
  LinkOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Tag } from "antd";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  clearExpiredSlotOpsOverrideThunk,
  fetchMeThunk,
} from "../Modules/User/Presentation/actions";
import { isSlotOpsOverrideActive } from "../Utils/slotOpsOverride";
import { canManageSlotOpsTimeSettings } from "../Utils/userPermissions";

dayjs.extend(utc);
dayjs.extend(timezone);

const Layout = styled.div`
  display: flex;
  height: 100vh;
  @media (max-width: 489px) {
    flex-direction: column;
  }
`;

/* Top bar for mobile */
const TopBar = styled.div`
  display: none;
  position: sticky;
  top: 0;
  z-index: 100;
  height: 56px;
  background: #111827;
  color: #fff;
  padding: 0 8px;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 489px) {
    display: flex;
  }
`;

const TopBarTitle = styled.div`
  font-weight: 700;
`;

const TopBarButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 20px;
  padding: 8px;
`;

const MobileBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: 90;
  display: none;
  @media (max-width: 489px) {
    display: block;
  }
`;

const MobileMenu = styled.aside`
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  bottom: 0;
  background: #111827;
  color: #fff;
  z-index: 100;
  padding: 14px 12px;
  overflow-y: auto;
`;

const Sidebar = styled.aside<{ collapsed: boolean }>`
  width: ${({ collapsed }) => (collapsed ? "72px" : "240px")};
  flex: 0 0 ${({ collapsed }) => (collapsed ? "72px" : "240px")};
  background: #111827;
  color: #fff;
  display: flex;
  flex-direction: column;
  transition: width 0.22s ease;
  align-items: stretch;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 18px 0 34px rgba(15, 23, 42, 0.08);
`;

const Logo = styled.h1<{ collapsed: boolean }>`
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: 0;
  padding: 24px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ collapsed }) =>
    collapsed &&
    css`
      text-align: center;
      font-size: 1.2rem;
      padding: 24px 0;
    `}
`;

const Menu = styled.nav<{ collapsed?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: ${({ collapsed }) => (collapsed ? "center" : "stretch")};
  padding: ${({ collapsed }) => (collapsed ? "8px 0 16px" : "8px 12px 16px")};
`;

const Item = styled(NavLink)<{ collapsed: boolean }>`
  box-sizing: border-box;
  width: ${({ collapsed }) => (collapsed ? "40px" : "100%")};
  min-height: 44px;
  height: ${({ collapsed }) => (collapsed ? "40px" : "auto")};
  padding: ${({ collapsed }) => (collapsed ? "0" : "0 12px")};
  color: #cbd5e1;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: ${({ collapsed }) => (collapsed ? "0" : "10px")};
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  transition: background 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;

  .anticon {
    flex: 0 0 auto;
    font-size: 16px;
  }

  &.active {
    background: #2563eb;
    color: #fff;
    box-shadow: 0 8px 18px rgba(37, 99, 235, 0.28);

    &:hover {
      background: #2563eb;
    }
  }
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
  }
`;

const SubItem = styled(NavLink)<{ collapsed: boolean }>`
  box-sizing: border-box;
  min-height: 38px;
  padding: ${({ collapsed }) => (collapsed ? "0" : "0 12px 0 38px")};
  color: #94a3b8;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: ${({ collapsed }) => (collapsed ? "0" : "10px")};
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  transition: background 0.16s ease, color 0.16s ease;

  .anticon {
    flex: 0 0 auto;
    font-size: 14px;
  }

  &.active {
    background: #2563eb;
    color: #fff;
    box-shadow: 0 8px 18px rgba(37, 99, 235, 0.28);

    &:hover {
      background: #2563eb;
    }
  }
  &:hover {
    background: rgba(255, 255, 255, 0.07);
    color: #fff;
  }
`;

const SubMenuContainer = styled.div<{ collapsed: boolean; isOpen: boolean }>`
  display: ${({ collapsed, isOpen }) =>
    collapsed ? "none" : isOpen ? "block" : "none"};
  transition: all 0.3s ease;
`;

const MenuHeader = styled.div<{
  collapsed: boolean;
  isOpen: boolean;
  $active?: boolean;
}>`
  box-sizing: border-box;
  width: ${({ collapsed }) => (collapsed ? "40px" : "100%")};
  min-height: 44px;
  height: ${({ collapsed }) => (collapsed ? "40px" : "auto")};
  padding: ${({ collapsed }) => (collapsed ? "0" : "0 12px")};
  color: ${({ $active }) => ($active ? "#fff" : "#cbd5e1")};
  display: flex;
  align-items: center;
  gap: ${({ collapsed }) => (collapsed ? "0" : "10px")};
  justify-content: ${({ collapsed }) =>
    collapsed ? "center" : "space-between"};
  cursor: pointer;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  background: ${({ $active }) => ($active ? "#2563eb" : "transparent")};
  box-shadow: ${({ $active }) =>
    $active ? "0 8px 18px rgba(37, 99, 235, 0.28)" : "none"};
  transition: background 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;

  .anticon {
    flex: 0 0 auto;
    font-size: 16px;
  }

  &:hover {
    background: ${({ $active }) =>
      $active ? "#2563eb" : "rgba(255, 255, 255, 0.08)"};
    color: #fff;
  }
`;

const Label = styled.span<{ collapsed: boolean }>`
  display: ${({ collapsed }) => (collapsed ? "none" : "inline")};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MenuHeaderText = styled.span<{ collapsed: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  overflow: hidden;

  ${({ collapsed }) =>
    collapsed &&
    css`
      gap: 0;
      justify-content: center;
    `}
`;

const LogoToggleWrapper = styled.div<{ collapsed: boolean }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "space-between")};
  width: 100%;
  min-height: 82px;
  padding: ${({ collapsed }) => (collapsed ? "18px 0 10px" : "0 10px 0 0")};
`;

const ToggleButton = styled.button`
  box-sizing: border-box;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.06);
  border: none;
  border-radius: 8px;
  color: #cbd5e1;
  padding: 0;
  font-size: 16px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.16s ease, color 0.16s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
  }
`;

const LogoutButton = styled.button<{ collapsed?: boolean }>`
  box-sizing: border-box;
  background: none;
  border: none;
  color: #cbd5e1;
  min-height: 44px;
  padding: ${({ collapsed }) => (collapsed ? "0" : "0 12px")};
  margin-top: auto;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: ${({ collapsed }) => (collapsed ? "0" : "10px")};
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
  width: 100%;
  transition: background 0.16s ease, color 0.16s ease;

  .anticon {
    flex: 0 0 auto;
    font-size: 16px;
  }

  &:hover {
    background: rgba(239, 68, 68, 0.12);
    color: #fecaca;
  }
  text-align: left;
  font-size: 14px;
  font-weight: 600;
`;

const Content = styled.main`
  flex: 1;
  background: #f5f6fa;
  padding: 32px;
  overflow-y: auto;
  @media (max-width: 489px) {
    padding: 16px 8px;
  }
`;

const pageEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageTransition = styled.div`
  min-height: 100%;
  animation: ${pageEnter} 180ms ease-out;
  will-change: opacity, transform;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const AdminLayout = () => {
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [slotsSubmenuOpen, setSlotsSubmenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // 브라우저 탭으로 돌아올 때 변경된 시간 제한 정책을 반영한다.
  useEffect(() => {
    const refreshPolicyOnVisible = () => {
      if (document.visibilityState === "visible") {
        dispatch(fetchMeThunk());
      }
    };
    document.addEventListener("visibilitychange", refreshPolicyOnVisible);
    return () => {
      document.removeEventListener("visibilitychange", refreshPolicyOnVisible);
    };
  }, [dispatch]);

  // 슬롯 임시 해제 만료 감지 (슬롯 페이지 외에서도 동작)
  useEffect(() => {
    const expiresAt = user?.slot_ops_override?.expires_at;
    if (!isSlotOpsOverrideActive(user) || !expiresAt) return undefined;

    const expiryMs = dayjs(expiresAt).valueOf();
    const remainingMs = expiryMs - Date.now();
    if (remainingMs <= 0) {
      dispatch(clearExpiredSlotOpsOverrideThunk());
      return undefined;
    }

    const timer = window.setTimeout(() => {
      dispatch(clearExpiredSlotOpsOverrideThunk());
    }, remainingMs);

    return () => window.clearTimeout(timer);
  }, [
    dispatch,
    user?.slot_ops_override?.active,
    user?.slot_ops_override?.expires_at,
    user,
  ]);

  const handleLogout = () => {
    dispatch(logout());
    // 로그아웃 시 앱 상태를 완전히 초기화하기 위해 리로드(=새로고침)로 이동
    window.location.replace("/login");
  };

  // viewport 변화 감지
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 489;
      setIsMobile(mobile);
      if (!mobile) setMobileMenuOpen(false);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileMenuOpen((prev) => !prev);
    } else {
      setCollapsed((prev) => !prev);
    }
  };

  const toggleSlotsSubmenu = () => {
    if (!collapsed) {
      setSlotsSubmenuOpen((prev) => !prev);
    }
  };

  // 현재 경로에 따라 서브메뉴 자동 열기
  React.useEffect(() => {
    if (location.pathname.startsWith("/slots") && !collapsed) {
      setSlotsSubmenuOpen(true);
    }
  }, [location.pathname, collapsed]);

  // 라우트 변경 시 모바일 메뉴 닫기
  useEffect(() => {
    if (isMobile) setMobileMenuOpen(false);
  }, [location.pathname, isMobile]);

  const getTitle = () => {
    if (location.pathname.startsWith("/dashboard")) return "대시보드";
    if (location.pathname.startsWith("/notices")) return "공지사항";
    if (location.pathname.startsWith("/accounts")) return "계정관리";
    if (location.pathname.startsWith("/slots-inactive")) return "비활성 슬롯";
    if (location.pathname.startsWith("/slots")) return "슬롯관리";
    if (location.pathname.startsWith("/keyword-optimization"))
      return "키워드별 순위";
    if (location.pathname.startsWith("/url-rank")) return "URL 순위 조회";
    if (location.pathname.startsWith("/slack-notify-settings")) {
      return user?.username === "adpa99" ? "Slack 로그 알림 설정" : "스티젠";
    }
    if (location.pathname.startsWith("/slot-ops-time-settings")) {
      return "시간 제한 설정";
    }
    if (location.pathname.startsWith("/settings")) return "설정";
    if (location.pathname.startsWith("/logs")) return "로그관리";
    if (location.pathname.startsWith("/profile")) return "마이페이지";
    return "스티젠";
  };

  const isSlotsMenuActive = location.pathname.startsWith("/slots");
  const showSlotsGroupActive = collapsed && isSlotsMenuActive;

  return (
    <Layout>
      {user && (
        <>
          {/* Mobile AppBar */}
          {isMobile && (
            <TopBar>
              <TopBarButton onClick={toggleSidebar} aria-label="메뉴">
                <MenuFoldOutlined />
              </TopBarButton>
              <div style={{ fontWeight: 700 }}>{getTitle()}</div>
              <div />
            </TopBar>
          )}

          {/* Mobile Fullscreen Menu */}
          {isMobile && mobileMenuOpen && (
            <>
              <MobileBackdrop onClick={() => setMobileMenuOpen(false)} />
              <MobileMenu>
                <Menu collapsed={false}>
                  <Item
                    to="/dashboard"
                    collapsed={false}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <DashboardOutlined />
                    <Label collapsed={false}>대시보드</Label>
                  </Item>
                  <Item
                    to="/notices"
                    collapsed={false}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <NotificationOutlined />
                    <Label collapsed={false}>공지사항</Label>
                  </Item>
                  <Item
                    to="/accounts"
                    collapsed={false}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <TeamOutlined />
                    <Label collapsed={false}>계정관리</Label>
                  </Item>

                  {user.grade === "ADMIN" ? (
                    <>
                      <MenuHeader
                        collapsed={false}
                        isOpen={slotsSubmenuOpen}
                        $active={false}
                        onClick={toggleSlotsSubmenu}
                      >
                        <MenuHeaderText collapsed={false}>
                          <DatabaseOutlined />
                          <Label collapsed={false}>슬롯관리</Label>
                        </MenuHeaderText>
                        {slotsSubmenuOpen ? (
                          <DownOutlined style={{ fontSize: "12px" }} />
                        ) : (
                          <RightOutlined style={{ fontSize: "12px" }} />
                        )}
                      </MenuHeader>
                      <SubMenuContainer
                        collapsed={false}
                        isOpen={slotsSubmenuOpen}
                      >
                        <SubItem
                          to="/slots"
                          collapsed={false}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Label collapsed={false}>활성 슬롯</Label>
                        </SubItem>
                        <SubItem
                          to="/slots-inactive"
                          collapsed={false}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Label collapsed={false}>비활성 슬롯</Label>
                        </SubItem>
                      </SubMenuContainer>
                    </>
                  ) : (
                    <Item
                      to="/slots"
                      collapsed={false}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <DatabaseOutlined />
                      <Label collapsed={false}>슬롯관리</Label>
                    </Item>
                  )}

                  {user.grade === "ADMIN" && (
                    <>
                      <Item
                        to="/keyword-optimization"
                        collapsed={false}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <BarChartOutlined />
                        <Label collapsed={false}>키워드별 순위</Label>
                      </Item>
                      <Item
                        to="/url-rank"
                        collapsed={false}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <LinkOutlined />
                        <Label collapsed={false}>URL 순위 조회</Label>
                      </Item>
                      <Item
                        to="/settings"
                        collapsed={false}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <SettingOutlined />
                        <Label collapsed={false}>설정</Label>
                      </Item>
                    </>
                  )}

                  {canManageSlotOpsTimeSettings(user) && (
                    <Item
                      to="/slot-ops-time-settings"
                      collapsed={false}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <ClockCircleOutlined />
                      <Label collapsed={false}>시간 제한 설정</Label>
                    </Item>
                  )}

                  {user.username === "adpa99" && (
                    <Item
                      to="/slack-notify-settings"
                      collapsed={false}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FileTextOutlined />
                      <Label collapsed={false}>Slack 로그 알림 설정</Label>
                    </Item>
                  )}

                  <Item
                    to="/logs"
                    collapsed={false}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <FileTextOutlined />
                    <Label collapsed={false}>로그관리</Label>
                  </Item>
                  <Item
                    to="/profile"
                    collapsed={false}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <UserOutlined />
                    <Label collapsed={false}>마이페이지</Label>
                  </Item>
                  <LogoutButton
                    collapsed={false}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleLogout();
                    }}
                  >
                    <LogoutOutlined />
                    <Label collapsed={false}>로그아웃</Label>
                  </LogoutButton>
                </Menu>
              </MobileMenu>
            </>
          )}

          {/* Desktop Sidebar */}
          {!isMobile && (
            <Sidebar collapsed={collapsed}>
              <LogoToggleWrapper collapsed={collapsed}>
                {!collapsed && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      padding: "24px 0 24px 24px",
                    }}
                  >
                    <Logo
                      collapsed={collapsed}
                      style={{ padding: 0, fontWeight: 600 }}
                    >
                      스티젠
                    </Logo>
                    {user?.is_limited_partner_admin && (
                      <Tag
                        color="orange"
                        style={{
                          width: "fit-content",
                          marginTop: 4,
                          fontSize: "10px",
                        }}
                      >
                        제한형 관리자
                      </Tag>
                    )}
                    {isSlotOpsOverrideActive(user) &&
                      user?.slot_ops_override?.expires_at && (
                        <Tag
                          color="green"
                          style={{
                            width: "fit-content",
                            marginTop: 4,
                            fontSize: "10px",
                          }}
                        >
                          임시 해제 ~{" "}
                          {dayjs(user.slot_ops_override.expires_at)
                            .tz("Asia/Seoul")
                            .format("HH:mm")}
                        </Tag>
                      )}
                  </div>
                )}
                <ToggleButton onClick={toggleSidebar}>
                  {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                </ToggleButton>
              </LogoToggleWrapper>
              <Menu collapsed={collapsed}>
                <Item to="/dashboard" collapsed={collapsed}>
                  <DashboardOutlined />
                  <Label collapsed={collapsed}>대시보드</Label>
                </Item>
                <Item to="/notices" collapsed={collapsed}>
                  <NotificationOutlined />
                  <Label collapsed={collapsed}>공지사항</Label>
                </Item>
                <Item to="/accounts" collapsed={collapsed}>
                  <TeamOutlined />
                  <Label collapsed={collapsed}>계정관리</Label>
                </Item>

                {user.grade === "ADMIN" ? (
                  <>
                    <MenuHeader
                      collapsed={collapsed}
                      isOpen={slotsSubmenuOpen}
                      $active={showSlotsGroupActive}
                      onClick={() => {
                        if (collapsed) {
                          navigate("/slots");
                        } else {
                          toggleSlotsSubmenu();
                        }
                      }}
                    >
                      <MenuHeaderText collapsed={collapsed}>
                        <DatabaseOutlined />
                        <Label collapsed={collapsed}>슬롯관리</Label>
                      </MenuHeaderText>
                      {!collapsed &&
                        (slotsSubmenuOpen ? (
                          <DownOutlined style={{ fontSize: "12px" }} />
                        ) : (
                          <RightOutlined style={{ fontSize: "12px" }} />
                        ))}
                    </MenuHeader>
                    <SubMenuContainer
                      collapsed={collapsed}
                      isOpen={slotsSubmenuOpen}
                    >
                      <SubItem to="/slots" collapsed={collapsed}>
                        <Label collapsed={collapsed}>활성 슬롯</Label>
                      </SubItem>
                      <SubItem to="/slots-inactive" collapsed={collapsed}>
                        <Label collapsed={collapsed}>비활성 슬롯</Label>
                      </SubItem>
                    </SubMenuContainer>
                  </>
                ) : (
                  <Item to="/slots" collapsed={collapsed}>
                    <DatabaseOutlined />
                    <Label collapsed={collapsed}>슬롯관리</Label>
                  </Item>
                )}

                {user.grade === "ADMIN" && (
                  <>
                    <Item to="/keyword-optimization" collapsed={collapsed}>
                      <BarChartOutlined />
                      <Label collapsed={collapsed}>키워드별 순위</Label>
                    </Item>
                    <Item to="/url-rank" collapsed={collapsed}>
                      <LinkOutlined />
                      <Label collapsed={collapsed}>URL 순위 조회</Label>
                    </Item>
                    <Item to="/settings" collapsed={collapsed}>
                      <SettingOutlined />
                      <Label collapsed={collapsed}>설정</Label>
                    </Item>
                  </>
                )}

                {canManageSlotOpsTimeSettings(user) && (
                  <Item to="/slot-ops-time-settings" collapsed={collapsed}>
                    <ClockCircleOutlined />
                    <Label collapsed={collapsed}>시간 제한 설정</Label>
                  </Item>
                )}

                {user.username === "adpa99" && (
                  <Item to="/slack-notify-settings" collapsed={collapsed}>
                    <FileTextOutlined />
                    <Label collapsed={collapsed}>Slack 로그 알림 설정</Label>
                  </Item>
                )}

                <Item to="/logs" collapsed={collapsed}>
                  <FileTextOutlined />
                  <Label collapsed={collapsed}>로그관리</Label>
                </Item>
                <Item to="/profile" collapsed={collapsed}>
                  <UserOutlined />
                  <Label collapsed={collapsed}>마이페이지</Label>
                </Item>
                <LogoutButton collapsed={collapsed} onClick={handleLogout}>
                  <LogoutOutlined />
                  <Label collapsed={collapsed}>로그아웃</Label>
                </LogoutButton>
              </Menu>
            </Sidebar>
          )}
        </>
      )}
      <Content>
        <PageTransition key={location.pathname}>
          <Outlet />
        </PageTransition>
      </Content>
    </Layout>
  );
};

export default AdminLayout;
